import { TableNavigation, TablePaper } from 'components/common';
import { ExportCsv, ListTable } from 'components/core';
import { matchEnabledColumns } from 'components/core/ColumnPanel/utils';
import { useContext, useMemo } from 'react';
import { ALL_FILTERS } from 'utils/constants/Filters';
import { rowLayout, SHIPMENT_TABLE_HEADERS } from 'utils/constants/Shipment';
import {
  ColumnContext,
  FilterContext,
  ListContext,
  ViewContext,
} from 'utils/context';
import { useDetailsRedirect, usePermissions } from 'utils/hooks';

import { EntityEnum } from '@project44/entity-ui-utils';
import { CSS } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { ShipmentEntity } from '@project44-models/shipment-list';

import { EXPORT_RECORDS_LIMIT } from './constants';

interface ShipmentsTableProps {
  css: CSS;
  doSearch: () => void;
}

function ShipmentsTable({ css, doSearch }: ShipmentsTableProps) {
  const { t } = useTranslation();
  const handleRedirect = useDetailsRedirect();
  const permissions = usePermissions();
  const { clearFilters, hasFilters } = useContext(FilterContext);
  const { savedShipments } = useContext(ViewContext);
  const {
    doSearchShipments,
    isErrorShipments,
    isLoadingShipments,
    shipmentsData,
  } = useContext(ListContext);
  const { displayOptions } = useContext(ColumnContext);

  const [headers, rows] = useMemo(
    () =>
      matchEnabledColumns(
        SHIPMENT_TABLE_HEADERS,
        rowLayout,
        displayOptions?.activeColumns || [],
        permissions,
      ),
    [displayOptions?.activeColumns, permissions],
  );

  const onPageChange = (page: number) => {
    const added = (displayOptions?.activeColumns || [])
      .filter(({ id }) => /ref-/i.test(id))
      .map(({ label }) => label);
    doSearchShipments({ page, size: 20 }, added);
  };

  const handleClear = () => {
    clearFilters(ALL_FILTERS);
    doSearch();
  };

  const handleRowClick = ({ shipmentId }: ShipmentEntity) => {
    handleRedirect(EntityEnum.shipment, shipmentId);
  };

  const getEndItem = () => {
    const pageCount = shipmentsData.paginationInfo?.total || 0;
    const limit = pageCount > EXPORT_RECORDS_LIMIT;
    return <ExportCsv showLimitWarning={limit} />;
  };

  return (
    <TablePaper css={css}>
      <TableNavigation
        title={t('core.shipments')}
        resultsCount={shipmentsData.paginationInfo?.total || 0}
        css={{ marginBottom: '$medium' }}
        doSearch={doSearch}
        isLoading={isLoadingShipments}
        endItem={getEndItem()}
      />
      <ListTable
        title={t('core.shipments')}
        css={{ flex: '1 1 0' }}
        cssTable={{ flex: '1 1 0' }}
        data={shipmentsData.results}
        doSearch={doSearch}
        handleClear={handleClear}
        headers={headers}
        isError={isErrorShipments}
        isLoading={isLoadingShipments || savedShipments.isLoading}
        pagination={{ ...shipmentsData.paginationInfo, onPageChange }}
        rowClick={handleRowClick}
        rowLayout={rows}
        showClearButton={hasFilters(ALL_FILTERS)}
      />
    </TablePaper>
  );
}

export default ShipmentsTable;
